import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { windDirections } from './constants'

interface RoofDirectionAssignmentFormValue {
  shapes?: string[]
  configurations?: RoofConfig[]
}

export type RoofAssignmentFormValue = Partial<
  Record<WindDirection, RoofDirectionAssignmentFormValue>
>

interface RoofSlabForAssignment {
  guid: string
  shape: {
    points: Point[]
  }
}

interface CreateAutoDetermineAssignmentStrategyParams {
  roofSlabs: RoofSlabForAssignment[]
  currentRoofAssignment: RoofAssignmentFormValue
}

const FLAT_TOLERANCE = 1.0e-3
const COS_45_DEGREES = Math.cos(Math.PI / 4)
const E_Z = new ImmutableVector3(0, 0, 1)

const toImmutableVector3 = (vector: Point) => new ImmutableVector3(vector.x, vector.y, vector.z)

const toXY = (vector: ImmutableVector3) => new ImmutableVector3(vector.x, vector.y, 0)

const safeNormalize = (vector: ImmutableVector3) =>
  vector.length() === 0 ? vector : vector.normalize()

const isFlatNormal = (vector: ImmutableVector3) =>
  Math.abs(vector.x) < FLAT_TOLERANCE &&
  Math.abs(vector.y) < FLAT_TOLERANCE &&
  Math.abs(vector.z - 1) < FLAT_TOLERANCE

const getRoofNormal = (points: Point[]): ImmutableVector3 => {
  if (points.length < 3) return E_Z

  const p1 = toImmutableVector3(points[1])
  const p0 = toImmutableVector3(points[0])
  const p2 = toImmutableVector3(points[2])

  const edge1 = p1.sub(p0)
  const edge2 = p2.sub(p0)
  return safeNormalize(new ImmutableVector3().crossVectors(edge1, edge2))
}

const getUpwardsOrientedNormal = (normal: ImmutableVector3): ImmutableVector3 =>
  normal.z < 0 ? normal.multiplyScalar(-1) : normal

const getWindVectorByDirection = (direction: WindDirection): ImmutableVector3 => {
  switch (direction) {
    case 'Direction0':
      return new ImmutableVector3(1, 0, 0)
    case 'Direction90':
      return new ImmutableVector3(0, 1, 0)
    case 'Direction180':
      return new ImmutableVector3(-1, 0, 0)
    case 'Direction270':
      return new ImmutableVector3(0, -1, 0)
  }
}

const createFlatRoofConfiguration = (elementGuid: string): RoofConfig => ({
  element_guid: elementGuid,
  roof_type: 'FlatRoof',
})

const createGableRoofConfiguration = (
  elementGuid: string,
  flowAreaType: RoofAreaTypes,
): RoofConfig => ({
  element_guid: elementGuid,
  roof_type: 'GableRoof',
  flow_area_type: flowAreaType,
})

const determineFlowAreaType = (
  nUpwardsOriented: ImmutableVector3,
  windDirection: WindDirection,
): RoofAreaTypes => {
  const w = getWindVectorByDirection(windDirection)
  const wLeft = new ImmutableVector3().crossVectors(E_Z, w)
  const wRight = wLeft.multiplyScalar(-1)
  const wNeg = w.multiplyScalar(-1)
  const nXY = safeNormalize(toXY(nUpwardsOriented))

  const candidates: { flowAreaType: RoofAreaTypes; vector: ImmutableVector3 }[] = [
    { flowAreaType: 'Luv', vector: wNeg },
    { flowAreaType: 'Lee', vector: w },
    { flowAreaType: 'Left', vector: wLeft },
    { flowAreaType: 'Right', vector: wRight },
  ]

  const validCandidates = candidates
    .map(candidate => {
      const vectorXY = safeNormalize(toXY(candidate.vector))
      const projectedDot = nXY.dot(vectorXY)
      const fullDot = nUpwardsOriented.dot(candidate.vector)

      return {
        ...candidate,
        projectedDot,
        isValid: fullDot > 0 && projectedDot > COS_45_DEGREES,
      }
    })
    .filter(candidate => candidate.isValid)
    .sort((a, b) => b.projectedDot - a.projectedDot)

  if (validCandidates.length > 0) return validCandidates[0].flowAreaType

  // Fallback: pick the closest directional bucket in XY.
  return candidates
    .map(candidate => ({
      ...candidate,
      projectedDot: nXY.dot(safeNormalize(toXY(candidate.vector))),
    }))
    .sort((a, b) => b.projectedDot - a.projectedDot)[0].flowAreaType
}

const createConfigurationForSlab = (
  roofSlab: RoofSlabForAssignment,
  direction: WindDirection,
): RoofConfig => {
  const normal = getRoofNormal(roofSlab.shape.points)
  const nUpwardsOriented = getUpwardsOrientedNormal(normal)

  if (isFlatNormal(nUpwardsOriented)) return createFlatRoofConfiguration(roofSlab.guid)

  return createGableRoofConfiguration(
    roofSlab.guid,
    determineFlowAreaType(nUpwardsOriented, direction),
  )
}

export const createAutoDetermineAssignmentStrategy = ({
  roofSlabs,
  currentRoofAssignment,
}: CreateAutoDetermineAssignmentStrategyParams): RoofAssignmentFormValue =>
  windDirections.reduce<RoofAssignmentFormValue>(
    (roofAssignment, direction) => ({
      ...roofAssignment,
      [direction]: {
        ...roofAssignment?.[direction],
        configurations: roofSlabs.map(roofSlab => createConfigurationForSlab(roofSlab, direction)),
      },
    }),
    currentRoofAssignment,
  )
